import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '@/components/feature/LanguageToggle';

interface IntroSectionProps {
  onStart: () => void;
}

export default function IntroSection({ onStart }: IntroSectionProps) {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative min-h-dvh w-full overflow-hidden flex flex-col">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/08cee76ec93e833211818d60d7803219.jpg"
          alt="바른사주 배경"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/75" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 pt-8 pb-4">
        <img
          src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png"
          alt="바른사주 로고"
          className="h-10 object-contain"
        />
        <LanguageToggle />
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-16 text-center">
        <div
          className="transition-all duration-1000"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-white/90 text-xs font-medium tracking-wider">{t('intro.badge')}</span>
          </div>

          <h1
            className="text-white font-bold leading-tight mb-6 whitespace-pre-line"
            style={{ fontSize: 'clamp(1.8rem, 5vw, 3rem)' }}
          >
            {t('intro.title')}
          </h1>

          <p className="text-white/70 text-base md:text-lg max-w-md mx-auto leading-relaxed mb-2">
            {t('intro.subtitle1')}
          </p>
          <p className="text-white/70 text-base md:text-lg max-w-md mx-auto leading-relaxed mb-12">
            {t('intro.subtitle2')}<strong className="text-amber-300">{t('intro.highlight')}</strong>{t('intro.subtitle3')}
          </p>

          <button
            onClick={onStart}
            className="group relative inline-flex items-center gap-3 bg-white text-gray-900 font-bold text-base px-8 py-4 rounded-2xl transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer whitespace-nowrap shadow-2xl"
          >
            <span>{t('intro.cta')}</span>
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-900 group-hover:translate-x-1 transition-transform">
              <i className="ri-arrow-right-line text-white text-xs" />
            </span>
          </button>

          <p className="mt-5 text-white/40 text-xs">{t('intro.free')}</p>
        </div>
      </main>

      <footer className="relative z-10 px-6" style={{ paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom, 2.5rem))' }}>
        <div
          className="flex items-center justify-center gap-6 md:gap-10 transition-all duration-1000 delay-300"
          style={{ opacity: visible ? 1 : 0 }}
        >
          {[
            { icon: 'ri-compass-3-line', key: 'intro.feat1' },
            { icon: 'ri-history-line', key: 'intro.feat2' },
            { icon: 'ri-sun-line', key: 'intro.feat3' },
          ].map((item) => (
            <div key={item.key} className="flex flex-col items-center gap-1.5">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${item.icon} text-white/50 text-lg`} />
              </div>
              <span className="text-white/40 text-xs text-center whitespace-nowrap">{t(item.key)}</span>
            </div>
          ))}
        </div>
      </footer>
    </div>
  );
}
